import Image from 'next/image'

import type { CollectionProducts } from '@lib/types'

type Props = {
	products: CollectionProducts
	moreTitle?: string
	moreSubtext?: string
}

type CardProps = {
	product: CollectionProducts[number]
	mobileSize?: boolean
}

type MoreCardProps = {
	title: string
	subtext: string
	mobileSize?: boolean
}

const MOBILE_CARD_CLASSES = 'flex-shrink-0 snap-start w-[82vw] max-w-xs'

function ProductCard({ product, mobileSize }: CardProps) {
	const comparePrice =
		product.compareAtPrice && parseFloat(product.compareAtPrice.amount) > 0
			? product.compareAtPrice
			: null

	return (
		<div className={mobileSize ? MOBILE_CARD_CLASSES : ''}>
			{product.images[0] && (
				<div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-50">
					<Image
						src={product.images[0].url}
						alt={product.images[0].altText ?? product.vendor ?? 'Product image'}
						fill
						className="object-contain"
						sizes="(max-width: 640px) 82vw, (max-width: 768px) 320px, 200px"
					/>
					{comparePrice && (
						<span className="absolute top-2 left-2 text-[10px] font-bold tracking-wide bg-white/75 text-bbx-style backdrop-blur-sm px-2 py-0.5 rounded leading-none">
							${Math.round(parseFloat(comparePrice.amount))} VALUE
						</span>
					)}
				</div>
			)}

			<p className="mt-2 text-[11px] font-semibold text-gray-800 text-center leading-snug line-clamp-1">
				{product.vendor}
			</p>
		</div>
	)
}

// eslint-disable-next-line react/no-multi-comp
function MoreCard({ title, subtext, mobileSize }: MoreCardProps) {
	return (
		<div className={mobileSize ? MOBILE_CARD_CLASSES : ''}>
			<div className="w-full aspect-square rounded-xl bg-gray-50 flex flex-col items-center justify-center text-center px-4">
				<p className="font-serif text-3xl font-medium text-bbx-style leading-none">{title}</p>
				{/* gray-500 (not gray-450): the 450 token only meets WCAG AA on pure white, not on gray-50 */}
				<p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
					{subtext}
				</p>
			</div>
		</div>
	)
}

// eslint-disable-next-line react/no-multi-comp
export default function LandingProductCarousel({
	products,
	moreTitle = '& plenty more',
	moreSubtext = 'to choose from'
}: Props) {
	// When the collection holds more than we can show, the last slot becomes
	// an "& more" card — only then is the claim truthful.
	const hasMore = products.length > 8
	const displayProducts = products.slice(0, hasMore ? 7 : 8)

	return (
		<>
			{/* Mobile: horizontal snap-scroll carousel (< md) */}
			<div className="md:hidden relative">
				<div
					className="[&::-webkit-scrollbar]:hidden flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 pl-4 pr-4 scroll-pl-4 scroll-pr-4"
					style={{ scrollbarWidth: 'none' }}
				>
					{displayProducts.map((product) => (
						<ProductCard key={product.id} product={product} mobileSize />
					))}
					{hasMore && <MoreCard title={moreTitle} subtext={moreSubtext} mobileSize />}
				</div>
			</div>

			{/* Desktop: 4-column grid (md+) */}
			<div className="hidden md:block px-6">
				<div className="grid grid-cols-4 gap-x-6 gap-y-4 max-w-4xl mx-auto">
					{displayProducts.map((product) => (
						<ProductCard key={product.id} product={product} />
					))}
					{hasMore && <MoreCard title={moreTitle} subtext={moreSubtext} />}
				</div>
			</div>
		</>
	)
}
